<script setup lang="ts">
import { computed } from 'vue'
import {
  itemScopeAttributes,
  useModelStrings,
  type DetailTemplateProps,
} from '@jantm/model-core'
import type {Schedule} from "@/types/Wiser/schedule.ts";
import DocumentsComponent from "@/components/DocumentsComponent.vue";

const props = withDefaults(defineProps<DetailTemplateProps<Schedule>>(), { headingLevel: 2 })

const { t, core, locale, detailLabel } = useModelStrings(() => props.definition)

const scope = computed(() => itemScopeAttributes(props.definition))
const heading = computed(() => `h${props.headingLevel}` as const)
const regionLabel = computed(() => props.ariaLabel ?? detailLabel.value)
</script>

<template>
  <section
    class="wiser-detail schedule-detail"
    role="region"
    :aria-label="regionLabel"
    :aria-busy="loading && Boolean(entity)"
    v-bind="scope"
  >
    <p
      v-if="error"
      class="wiser-detail__error schedule-detail__error"
      role="alert"
    >
      {{ core('state.error', { fallback: 'Something went wrong.' }) }}
    </p>
    <p
      v-else-if="loading && !entity"
      role="status"
    >
      {{ core('state.loading', { fallback: 'Loading…' }) }}
    </p>
    <p
      v-else-if="!entity"
      role="status"
    >
      {{ core('state.notFound', { fallback: 'Not found.' }) }}
    </p>

    <template v-else>
      <!--
        The brand and model together are the accessible name of this record, so
        they are the heading — not decoration above it. The heading level comes
        from the caller so the page outline stays intact wherever it is used.
      -->
      <component
        :is="heading"
        class="wiser-detail__title schedule-detail__title"
      >
        <span>{{ entity.title }}</span>
      </component>

      <DocumentsComponent :entityId="entity.id" />
    </template>
  </section>
</template>
